// ---------------------------------------------------------------------------
// SINGLE SOURCE OF TRUTH for all site copy & data.
// Everything below is realistic placeholder content — edit it to make the
// portfolio yours. Search for "TODO" to find the spots that most need your
// real details (project links, dates, the bio specifics, etc.).
// ---------------------------------------------------------------------------

export interface Social {
  label: string;
  url: string;
  handle: string;
}

export interface Project {
  title: string;
  blurb: string;
  description: string;
  tech: string[];
  liveUrl?: string;
  githubUrl?: string;
  year: string;
  featured?: boolean;
}

export interface SkillGroup {
  title: string;
  items: string[];
}

export interface Experience {
  role: string;
  company: string;
  type?: string; // Full-time, Contract, Freelance
  period: string; // e.g. "Mar 2024 — Present"
  duration?: string; // e.g. "2 yrs 4 mos"
  location: string; // e.g. "Ghana · Remote"
  description?: string;
  bullets?: string[];
  tags?: string[];
  current?: boolean;
}

export interface Stat {
  value: number;
  suffix?: string;
  label: string;
}

export interface Highlight {
  title: string;
  description: string;
}

export const profile = {
  name: 'Arnold Quist',
  // The short label shown in the nav / footer wordmark.
  wordmark: 'ARNOLD',
  // Hero
  role: 'Software Engineer',
  // The typed terminal line in the hero. Keep it short & true.
  typedLines: [
    'building thoughtful interfaces.',
    'shipping reliable systems.',
    'sweating the details.',
  ],
  headline: ['I design and build', 'software with intent'],
  intro:
    "I'm a software engineer focused on the craft of the web - performant front-ends, " +
    'clean APIs, and interfaces that feel effortless. I care about the details most people ' +
    'never notice but everyone feels.',
  email: 'arnold@edenstark.com',
  location: 'Worldwide · Remote',
  // TODO: confirm/replace handles + URLs.
  socials: [
    { label: 'GitHub', url: 'https://github.com/09arnold', handle: '@09arnold' },
    { label: 'LinkedIn', url: 'https://linkedin.com/in/arnoldquist', handle: 'arnoldquist' },
    { label: 'Twitter', url: 'https://twitter.com/09arnold', handle: '@09arnold' },
  ] as Social[],
};

export const aboutBio = [
  // "I'm a developer with a designer's eye and an engineer's discipline. Over the years I've " +
  //   'shipped products end-to-end - from the first wireframe to production monitoring — and I ' +
  //   'enjoy every layer of that stack.',
  // 'My favorite work lives at the intersection of speed and polish: building things that load ' +
  //   'fast, scale gracefully, and feel genuinely pleasant to use. I believe great software is ' +
  //   'mostly small decisions made well, repeatedly.',
  "I’m a full-stack developer who enjoys turning ideas into products people actually enjoy using." +  
    "I’ve worked across the entire development process, from early design concepts to deployment and ongoing improvements.",
  "I care about building software that’s fast, reliable, and thoughtfully designed. For me, the best products come from" + 
    "paying attention to the small details and getting the fundamentals right."
];

export const highlights: Highlight[] = [
  {
    title: 'Product-minded',
    description:
      'I think in terms of users and outcomes, not just tickets — and I push back when something can be simpler.',
  },
  {
    title: 'Detail-obsessed',
    description:
      'Pixel alignment, easing curves, empty states, loading skeletons. The 1% that makes work feel finished.',
  },
  {
    title: 'Reliable by default',
    description:
      'Typed, tested, observable. I ship code I am happy to be paged for at 3am — because I rarely am.',
  },
];

// TODO: replace these with your real numbers.
export const stats: Stat[] = [
  { value: 9, suffix: '+', label: 'Years building' },
  { value: 14, suffix: '+', label: 'Projects shipped' },
  { value: 4, label: 'Open-source repos' },
  { value: 99, suffix: '%', label: 'Uptime mindset' },
];

export const skillGroups: SkillGroup[] = [
  {
    title: 'Frontend',
    items: ['React', 'Next.js', 'Redux', 'TypeScript', 'MUI', 'antd'],
  },
  {
    title: 'Backend',
    items: ['Node.js', 'Java', 'Spring', 'Laravel', 'PHP', 'REST'],
  },
  {
    title: 'Data & Cloud',
    items: ['MySQL', 'PostgreSQL', 'MongoDB', 'AWS', 'Docker', 'Git', 'CI/CD'],
  },
  {
    title: 'Leadership & Craft',
    items: ['Team Leadership', 'Team Management', 'Agile', 'Project Management', 'UI Design', 'Accessibility'],
  },
];

// Strip of tech shown in the slow marquee.
export const marqueeTech = [
  'React', 'Next.js', 'TypeScript', 'Node.js', 'Spring', 'Laravel',
  'PHP', 'MySQL', 'PostgreSQL', 'MongoDB', 'AWS', 'Docker', 'Git', 'Figma',
];

// TODO: replace with your real projects + links.
export const projects: Project[] = [
  {
    title: 'VolaChat',
    blurb: 'Private chat and calling',
    description:
      'WebRTC based chat application powered by PeerJS. Messages are direct between users' + 
      'No servers involved!',
    tech: ['WebRTC', 'React', 'PeerJS'],
    liveUrl: 'https://dc.edenstark.com',
    githubUrl: '#',
    year: '2025',
    featured: true,
  },
  {
    title: 'Enterprise Nurs',
    blurb: 'Unified Dashboard for Enterprises',
    description:
      'Micro-services platform with systems and transactions monitoring, live notifications, ' +
      ' data crawling and more.',
    tech: ['PolymerJS', 'Java', 'MongoDB', 'Akka'],
    liveUrl: '#',
    githubUrl: '#',
    year: '2017',
    featured: true,
  },
  {
    title: 'MyTXTBuddy',
    blurb: 'Bulk SMS platform',
    description:
      'A cloud messaging platform for sending SMS at scale. I worked primarily on the ' +
      'front-end — the dashboard and campaign tooling.',
    tech: ['JavaScript', 'HTML5', 'CSS', 'jQuery'],
    year: '2015',
  },
];

export const experience: Experience[] = [
  {
    role: 'Head of Service Development',
    company: 'Halges (Korba)',
    type: 'Full-time',
    period: 'Mar 2024 — Present',
    duration: '2 yrs 4 mos',
    location: 'Ghana · On-site',
    description:
      'Lead service development and engineering, owning delivery end-to-end and growing the team.',
    tags: ['Full-Stack Development', 'Team Leadership'],
    current: true,
  },
  {
    role: 'Software Engineer',
    company: 'Freelance',
    type: 'Freelance',
    period: 'Mar 2023 — Present',
    duration: '3 yrs 4 mos',
    location: 'Ghana · Remote',
    description:
      'Build web and product engineering for clients, from early concept through deployment.',
    tags: ['Software Development', 'Agile Methodologies'],
    current: true,
  },
  {
    role: 'Software Engineer',
    company: 'ON24',
    type: 'Contract',
    period: 'Apr 2021 — Jan 2023',
    duration: '1 yr 10 mos',
    location: 'Remote',
    description:
      'Engineering on a webinar and virtual-events platform, with a focus on delivery pipelines.',
    tags: ['CI/CD', 'Software Development'],
  },
  {
    role: 'Senior Software Engineer',
    company: 'Newton Classroom',
    type: 'Contract',
    period: 'Jan 2021 — Jun 2021',
    duration: '6 mos',
    location: 'Hyderabad, India · Remote',
    description:
      'Led and built parts of Newton (UI and API), a unified educational platform with Google ' +
      'Classroom integration — a dynamic reporting module, email/SMS messaging to parents and ' +
      'students, and a back-end refactor for reusability.',
    tags: ['Software Development', 'JavaScript'],
  },
  {
    role: 'Full Stack Engineer',
    company: 'Remo.co',
    type: 'Contract',
    period: 'Aug 2020 — Jan 2021',
    duration: '6 mos',
    location: 'Ghana · Remote',
    description:
      'Full-stack engineering on a virtual-events and live collaboration platform.',
    tags: ['Software Development', 'Agile Methodologies'],
  },
  {
    role: 'Senior Software Engineer',
    company: 'Andela',
    period: 'Jan 2019 — Mar 2020',
    duration: '1 yr 3 mos',
    location: 'Ghana · Remote',
    description:
      'Senior engineer building software for distributed teams across global clients.',
    tags: ['Software Development', 'Agile Methodologies'],
  },
  {
    role: 'Software Engineer',
    company: 'DreamOval Ltd.',
    type: 'Full-time',
    period: 'Jul 2013 — Dec 2018',
    duration: '5 yrs 6 mos',
    location: 'Accra, Ghana',
    description:
      'Built and shipped fintech and payment products, where I cut my teeth on production systems.',
    tags: ['CI/CD', 'Software Development'],
  },
];

export const navItems = [
  { label: 'about', id: 'about' },
  { label: 'skills', id: 'skills' },
  { label: 'experience', id: 'experience' },
  { label: 'work', id: 'projects' },
  { label: 'contact', id: 'contact' },
];
